'use client'

/**
 * PinkBallSlider — designer-made CAPTCHA
 *
 * Visuaalne anti-spam: kasutaja peab roosa palli paremasse otsa lohistama.
 * Brand color #F9C8C8, soft minimal stiil, mänguline animation.
 * Töötab nii touch (mobile) kui mouse (desktop) sündmustega.
 *
 * Botid ei oska drag event'e lihtsalt simuleerida — peavad mousemove + mouseup'i
 * realistlikult järjestama.
 */

import { useEffect, useRef, useState } from 'react'

interface Props {
  onVerified: () => void
  label?: string
  successLabel?: string
}

export function PinkBallSlider({
  onVerified,
  label = 'Lohista pall paremasse otsa →',
  successLabel = '✓ Kinnitatud',
}: Props) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState(0) // 0..1
  const [dragging, setDragging] = useState(false)
  const [verified, setVerified] = useState(false)
  const startX = useRef(0)
  const startPosition = useRef(0)

  const BALL_SIZE = 56
  const TRACK_PADDING = 4

  const handleStart = (clientX: number) => {
    if (verified) return
    setDragging(true)
    startX.current = clientX
    startPosition.current = position
  }

  const handleMove = (clientX: number) => {
    if (!dragging || !trackRef.current || verified) return
    const trackWidth = trackRef.current.offsetWidth
    const maxTravel = trackWidth - BALL_SIZE - TRACK_PADDING * 2
    const delta = clientX - startX.current
    const newPosition = Math.max(0, Math.min(1, startPosition.current + delta / maxTravel))
    setPosition(newPosition)
  }

  const handleEnd = () => {
    if (!dragging) return
    setDragging(false)
    if (position > 0.92) {
      setPosition(1)
      setVerified(true)
      onVerified()
    } else {
      // Snap back smoothly
      setPosition(0)
    }
  }

  useEffect(() => {
    if (!dragging) return
    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX)
    const onMouseUp = () => handleEnd()
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) handleMove(e.touches[0].clientX)
    }
    const onTouchEnd = () => handleEnd()
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    window.addEventListener('touchmove', onTouchMove, { passive: false })
    window.addEventListener('touchend', onTouchEnd)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend', onTouchEnd)
    }
  }, [dragging, position])

  const trackWidth = trackRef.current?.offsetWidth || 320
  const maxTravel = trackWidth - BALL_SIZE - TRACK_PADDING * 2
  const ballLeft = TRACK_PADDING + position * maxTravel
  const fillWidth = ballLeft + BALL_SIZE / 2

  return (
    <div className="w-full">
      <div
        ref={trackRef}
        className="relative w-full select-none rounded-full overflow-hidden"
        style={{
          height: BALL_SIZE + TRACK_PADDING * 2,
          background: verified ? '#111' : '#fff',
          border: verified ? '1px solid #111' : '1px solid rgba(0,0,0,0.08)',
          transition: 'background 250ms ease, border-color 250ms ease',
          touchAction: 'none',
        }}
      >
        {/* Pink fill that grows behind the ball */}
        {!verified && (
          <div
            className="absolute top-0 left-0 h-full pointer-events-none"
            style={{
              width: `${fillWidth}px`,
              background: 'linear-gradient(90deg, rgba(249,200,200,0.4) 0%, rgba(249,200,200,0.7) 100%)',
              transition: dragging ? 'none' : 'width 350ms cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        )}

        {/* Center label */}
        {!verified && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{
              fontSize: '13px',
              fontWeight: 500,
              color: position > 0.3 ? 'transparent' : 'rgba(0,0,0,0.45)',
              letterSpacing: '0.01em',
              transition: 'color 200ms ease',
              paddingLeft: BALL_SIZE,
            }}
          >
            {label}
          </div>
        )}

        {/* Verified label */}
        {verified && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#fff',
              letterSpacing: '0.02em',
              animation: 'fadeIn 250ms ease',
            }}
          >
            {successLabel}
          </div>
        )}

        {/* The pink ball */}
        <div
          onMouseDown={(e) => handleStart(e.clientX)}
          onTouchStart={(e) => e.touches[0] && handleStart(e.touches[0].clientX)}
          className="absolute top-1/2 rounded-full flex items-center justify-center"
          style={{
            left: `${ballLeft}px`,
            width: BALL_SIZE,
            height: BALL_SIZE,
            transform: 'translateY(-50%)',
            background: '#F9C8C8',
            cursor: verified ? 'default' : (dragging ? 'grabbing' : 'grab'),
            boxShadow: verified
              ? '0 0 0 3px #fff, 0 4px 16px rgba(249,200,200,0.4)'
              : '0 4px 14px rgba(249,200,200,0.6), 0 1px 3px rgba(0,0,0,0.08)',
            transition: dragging
              ? 'none'
              : 'left 350ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms ease',
          }}
        >
          {verified ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.55 }}>
              <polyline points="9 18 15 12 9 6" />
            </svg>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(2px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
